import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Icon, Divider } from 'semantic-ui-react';
import { colors } from 'utils/colors';

const MAX_YEARS = 50;
const TICKS = [0, 10, 20, 30, 40, 50];

const toPercent = (years) =>
  `${Math.max(0, Math.min(Number(years) / MAX_YEARS, 1)) * 100}%`;

const Result = ({ result, isFetching, riskFree, useDefault }) => {
  if (!result) return null;

  const isOnTrack = result.portLongevity >= result.lifeExpectancy;

  return (
    <div id="longevity-ref" style={{ opacity: isFetching ? 0.3 : 1 }}>
      <Divider
        style={{
          margin: '2rem auto',
          width: '80%',
          maxWidth: '800px',
          color: '#ececf0',
        }}
      />
      <Wrapper>
        <Heading>Results</Heading>

        <ChartCard>
          <Row>
            <RowHeader>
              <div>
                <Label color={colors.navyBlue}>Portfolio</Label>
                <LabelText> - It is expected to last you:</LabelText>
                {!riskFree && <Info>*based on 10,000 simulations</Info>}
              </div>
              <Value>{result.portLongevity} years</Value>
            </RowHeader>
            <Track>
              <Fill
                color={colors.navyBlue}
                style={{ width: toPercent(result.portLongevity) }}
              />
            </Track>
          </Row>

          <Row>
            <RowHeader>
              <div>
                <Label color={colors.blue}>You</Label>
                <LabelText>
                  {' '}
                  - You {useDefault ? 'are expected' : 'expect'} to live for
                  another:
                </LabelText>
                {useDefault && <Info>*based on your age &amp; gender</Info>}
              </div>
              <Value>{result.lifeExpectancy} years</Value>
            </RowHeader>
            <Track>
              <Fill
                color={colors.blue}
                style={{ width: toPercent(result.lifeExpectancy) }}
              />
            </Track>
          </Row>

          <Axis>
            {TICKS.map((tick, i) => (
              <Tick
                key={tick}
                style={{
                  left: `calc(${(tick / MAX_YEARS) * 100}% + (1px - ${
                    (tick / MAX_YEARS) * 2
                  }px))`,
                }}
              >
                <span className="mark" />
                <span className="label">
                  {tick}
                  {i === TICKS.length - 1 ? '+' : ''}
                </span>
              </Tick>
            ))}
          </Axis>
          <AxisTitle>Years from today</AxisTitle>
        </ChartCard>

        <Banner positive={isOnTrack}>
          <BannerIcon positive={isOnTrack}>
            <Icon name={isOnTrack ? 'check' : 'warning sign'} />
          </BannerIcon>
          <BannerBody>
            <BannerTitle>
              {isOnTrack
                ? 'Congratulations! You are not expected to outlive your savings.'
                : 'Oops! You may want to revisit your spending habits and/or investment portfolio.'}
            </BannerTitle>
            <BannerText>
              {isOnTrack
                ? 'With your current savings, chosen portfolio, and life expectancy, you could draw up to '
                : 'With your current savings, chosen portfolio, and life expectancy, you should not draw more than '}
              <strong>${result.maxSpending.toLocaleString()}</strong>
              {isOnTrack
                ? ' per year from your savings and equate your expected portfolio longevity to your expected human longevity.'
                : ' per year from your savings in order to equate your expected portfolio longevity to your expected human longevity.'}
            </BannerText>
          </BannerBody>
        </Banner>
      </Wrapper>
    </div>
  );
};

Result.propTypes = {
  result: PropTypes.object,
  isFetching: PropTypes.bool,
  riskFree: PropTypes.bool,
  useDefault: PropTypes.bool,
};

Result.defaultProps = {
  result: null,
  isFetching: false,
  riskFree: false,
  useDefault: true,
};

export default Result;

const Wrapper = styled.div`
  width: 80%;
  max-width: 800px;
  margin: 0 auto;
  padding-bottom: 1rem;
`;

const Heading = styled.h3`
  margin: 1.5rem 0 1rem 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #2b2b2b;
`;

const ChartCard = styled.div`
  background: #fff;
  border: 1px solid #e6e6ec;
  border-radius: 10px;
  padding: 1.5rem 1.75rem 1.25rem;
`;

const Row = styled.div`
  margin-bottom: 1.5rem;
`;

const RowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const Label = styled.span`
  font-weight: 700;
  font-size: 16px;
  color: ${(p) => p.color};
`;

const LabelText = styled.span`
  color: #333;
  font-size: 16px;
`;

const Info = styled.div`
  margin-top: 0.35rem;
  font-size: 15px;
  font-style: italic;
  color: rgba(0, 0, 0, 0.6);
`;

const Value = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.1;
  white-space: nowrap;
  color: #222;
`;

const Track = styled.div`
  height: 16px;
  background: #f0f0f3;
  border-radius: 4px;
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  background: ${(p) => p.color};
  border-radius: 4px;
  transition: width 0.6s ease;
`;

const Axis = styled.div`
  position: relative;
  height: 1.75rem;
  border-top: 2px solid #e6e6ec;
  margin-top: 0.5rem;
`;

const Tick = styled.div`
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;

  .mark {
    width: 2px;
    height: 6px;
    background: #e6e6ec;
  }

  .label {
    margin-top: 0.25rem;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.6);
  }
`;

const AxisTitle = styled.div`
  text-align: center;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
`;

const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  margin-top: 1.5rem;
  padding: 1.25rem 1.5rem;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.positive ? '#b9deaa' : '#f0707a')};
  background: ${(p) => (p.positive ? '#f7fbf1' : '#ffeaea')};
`;

const BannerIcon = styled.div`
  flex: 0 0 auto;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.positive ? '#e1f1d8' : 'transparent')};
  color: ${(p) => (p.positive ? '#2e7d32' : '#d32f2f')};

  i.icon {
    margin: 0;
    font-size: ${(p) => (p.positive ? '1rem' : '1.4rem')};
    line-height: 1;
  }
`;

const BannerBody = styled.div`
  flex: 1 1 auto;
`;

const BannerTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #222;
  margin-bottom: 0.5rem;
`;

const BannerText = styled.div`
  font-size: 16px;
  line-height: 1.55;
  color: #333;

  strong {
    font-weight: 700;
  }
`;
